import { CSSProperties, ForwardedRef, ReactNode, forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { Position, Range } from 'types'
import { DraggableContext } from 'ui'
import { gifFromMediaSprite } from '../../utils/media'
import { ArtboardContext } from './context'
import { useLayers } from './hooks/use-layers'
import { useRenderer } from './hooks/use-renderer'
import { useShader } from './hooks/use-shader'
import Loop from './loop'
import { Container, RasterizedCanvas } from './style'
import { ArtboardFilter, ArtboardLoopRef, ArtboardRasterizedRef, ArtboardRef } from './types'
import { createSprite } from './utilities/create-sprite'

export { default as GIF } from './layers/gif'
export { default as Image } from './layers/image'
export { default as Sprite } from './layers/sprite'
export { default as Text } from './layers/text'
export { default as Texture } from './layers/texture'

type Props = {
    children?: ReactNode
    className?: string
    duration: number
    filter?: ArtboardFilter
    filterIntensity?: number
    fps?: number
    height: number
    paused?: boolean
    style?: CSSProperties
    transparency?: boolean
    trim?: Range
    width: number
}

const Artboard = (
    {
        children,
        className,
        duration,
        filter,
        filterIntensity = 1,
        fps = 15,
        height,
        paused = false,
        style = {},
        transparency = false,
        trim,
        width,
    }: Props,
    ref: ForwardedRef<ArtboardRef>
) => {
    const { nodes, refs: layers } = useLayers(children)
    const canvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'))
    const loopRef = useRef<ArtboardLoopRef>(null)
    const rasterizedRef = useRef<ArtboardRasterizedRef>(null)
    const renderer = useRenderer(canvasRef.current, layers, transparency)
    const shader = useShader(renderer)
    const frames = useMemo(() => Math.floor(duration * fps), [duration])
    const delay = useMemo(() => 1 / fps, [fps])

    function onLoop(progress: number) {
        rasterizedRef.current?.renderer?.render(progress)
    }

    useImperativeHandle(ref, () => ({
        createGIF: async (crop?: Position) => {
            loopRef.current?.pause()

            const sprite = await createSprite(frames, fps, trim, rasterizedRef.current?.renderer, crop)
            const gif = sprite ? await gifFromMediaSprite(sprite) : null

            loopRef.current?.play()

            return gif
        },
        createSprite: async (crop?: Position) => {
            loopRef.current?.pause()

            const sprite = await createSprite(frames, fps, trim, rasterizedRef.current?.renderer, crop)

            loopRef.current?.play()

            return sprite
        },
        getProgress: () => {
            return loopRef.current?.progress || 0
        },
        setProgress: (progress: number) => {
            loopRef.current?.scrub(progress)
        },
    }))

    return (
        <ArtboardContext.Provider
            value={{ frames, filter, filterIntensity, fps, height, paused, renderer, shader, width }}
        >
            <Container className={className} style={{ height, width, ...style }}>
                <Loop delay={delay} paused={paused} frames={frames} ref={loopRef} trim={trim} onLoop={onLoop} />
                <RasterizedCanvas
                    duration={duration}
                    filter={filter}
                    filterIntensity={filterIntensity}
                    ref={rasterizedRef}
                    transparency={transparency}
                />
                <DraggableContext>{renderer && nodes}</DraggableContext>
            </Container>
        </ArtboardContext.Provider>
    )
}

export default forwardRef(Artboard)
